import { AppStoreAuthStore } from "@/store/auth/AppStoreAuthStore";
import { PublicAppStoreConstructorArgs, PublicAppSubStoreArgs } from "@/store/types";
import { makeAutoObservable, runInAction } from "mobx";
import { AppStoreInterfaceStore } from "@/store/interface/AppStoreInterfaceStore";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";
import { BaseError } from "@/domains/errors";
import { AppStoreDebugStore } from "@/store/debug/AppStoreDebugStore";
import { AppStoreElectronStore } from "@/store/electron/AppStoreElectronStore";
import { AppStoreSystemPermissionsStore } from "@/store/system-permissions";
import { CacheStore } from "@/modules/cache";
import { GLOBAL_CACHE_DB_IDENTIFIER } from "@/modules/cache/constants";

/**
 * A version of our AppStore which is accessible before the user is logged in.
 */
export class PublicAppStore {
  auth: AppStoreAuthStore;
  interface: AppStoreInterfaceStore;
  debug: AppStoreDebugStore;
  electron: AppStoreElectronStore;
  systemPermissions: AppStoreSystemPermissionsStore;
  globalCache: CacheStore;

  initializationState: AsyncResult<boolean> = asyncResultModule.setLoading();

  constructor({ api, pusher }: PublicAppStoreConstructorArgs) {
    const injectedDeps: PublicAppSubStoreArgs = { store: this, api, pusher };

    // This should be first, so other stores can access it immediately.
    this.globalCache = new CacheStore({
      databaseId: GLOBAL_CACHE_DB_IDENTIFIER,
    });

    // This is second, because it exposes some global environment info (like isDesktop, etc.)
    this.interface = new AppStoreInterfaceStore(injectedDeps);

    // This is third, so the electron-related metadata and handlers
    // are available immediately.
    this.electron = new AppStoreElectronStore(injectedDeps);

    this.auth = new AppStoreAuthStore(injectedDeps);
    this.debug = new AppStoreDebugStore(injectedDeps);
    this.systemPermissions = new AppStoreSystemPermissionsStore(injectedDeps);

    makeAutoObservable(this);
  }

  async initialize() {
    try {
      runInAction(() => {
        this.initializationState = asyncResultModule.setLoading();
      });

      await this.globalCache.initialize();
      await this.electron.initialize();
      await this.interface.initialize();
      await this.debug.initialize();
      await this.auth.initialize();

      runInAction(() => {
        this.initializationState = asyncResultModule.setReady(true);
      });
    } catch (unknownErr) {
      const err = unknownErr as BaseError;

      runInAction(() => {
        this.initializationState = asyncResultModule.setError(err);
      });

      throw err;
    }
  }

  useInitializePublicStoreEffects({
    searchParams,
    setSearchParams,
  }: {
    searchParams: URLSearchParams;
    setSearchParams: (searchParams: URLSearchParams) => void;
  }) {
    this.interface.useScreenSizeUpdater();
    this.auth.processSearchParams({ searchParams, setSearchParams });
  }
}
